import React, { useState } from 'react';
import { useMatchStore } from '../../stores/matchStore';
import { MatchResult as MatchResultType, Match } from '../../types/match';
import { TrophyIcon, HandRaisedIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';

interface MatchResultProps {
  match: Match;
  onResultSaved?: () => void;
  onCancel?: () => void;
  className?: string;
}

const MatchResult: React.FC<MatchResultProps> = ({ 
  match, 
  onResultSaved, 
  onCancel,
  className = '' 
}) => {
  const { completeMatch, isLoading, error, clearError } = useMatchStore();

  const [selectedResult, setSelectedResult] = useState<MatchResultType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleResultSelect = (result: MatchResultType) => {
    setSelectedResult(result);
    clearError();
  };

  const handleSubmit = () => {
    if (!selectedResult) return;
    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    if (!selectedResult) return;

    setIsSubmitting(true);
    clearError();

    try {
      // 完成比赛，更新结果
      await completeMatch(match.id, selectedResult);
      
      setShowConfirmation(false);
      onResultSaved?.();
    } catch (err) {
      console.error('保存比赛结果失败:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (showConfirmation) {
      setShowConfirmation(false);
    } else {
      onCancel?.();
    }
  };

  const getResultDescription = (result: MatchResultType) => {
    switch (result) {
      case 'win':
        return '我方团队获胜';
      case 'draw':
        return '平局';
      case 'loss':
        return '我方团队失败';
      default:
        return '';
    }
  };

  const getResultIcon = (result: MatchResultType) => {
    switch (result) {
      case 'win':
        return <TrophyIcon className="w-8 h-8 text-blue-600" />;
      case 'draw':
        return <HandRaisedIcon className="w-8 h-8 text-yellow-600" />;
      case 'loss':
        return <TrophyIcon className="w-8 h-8 text-green-600" />;
      default:
        return null;
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h2 className="text-xl font-bold text-gray-800 mb-6">记录比赛结果</h2>

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="text-red-600 text-sm">{error}</div>
            <button
              onClick={clearError}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* 比赛配对回顾 */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-700 mb-4">本场比赛团队</h3>
        <div className="flex items-center justify-center">
          <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-6 min-w-[300px]">
            <div className="text-blue-800 font-semibold mb-4 text-center text-lg">我方团队</div>
            <div className="grid grid-cols-2 gap-4">
              {/* 搭档1 */}
              <div className="text-center">
                <div className="text-sm text-blue-600 mb-1">搭档组合1</div>
                {match.pairing.team1.map((player) => (
                  <div key={player.id} className="text-sm">
                    <div className="font-medium">{player.name}</div>
                    <div className="text-xs text-blue-600">#{player.number}</div>
                  </div>
                ))}
              </div>
              
              {/* 搭档2 */}
              <div className="text-center">
                <div className="text-sm text-blue-600 mb-1">搭档组合2</div>
                {match.pairing.team2.map((player) => (
                  <div key={player.id} className="text-sm">
                    <div className="font-medium">{player.name}</div>
                    <div className="text-xs text-blue-600">#{player.number}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="text-center mt-4 pt-3 border-t border-blue-200">
              <div className="text-sm text-blue-700">
                vs 对手团队
              </div>
            </div>
          </div>
        </div>
      </div>

      {!showConfirmation ? (
        <>
          {/* 结果选择 */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 mb-4">选择比赛结果</h3>
            <div className="text-center mb-4">
              <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-lg">
                <span className="text-blue-800 font-medium">
                  我方团队：{[...match.pairing.team1, ...match.pairing.team2].map(p => p.name).join(' & ')}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* 我方获胜 */}
              <button
                onClick={() => handleResultSelect('win')}
                className={`p-6 rounded-lg border-2 transition-all ${
                  selectedResult === 'win'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                }`}
              >
                <div className="text-center">
                  <TrophyIcon className="w-12 h-12 mx-auto mb-3 text-green-600" />
                  <div className="font-semibold text-green-800 mb-1">我方获胜</div>
                  <div className="text-sm text-gray-600">团队胜利</div>
                </div>
              </button>

              {/* 平局 */}
              <button
                onClick={() => handleResultSelect('draw')}
                className={`p-6 rounded-lg border-2 transition-all ${
                  selectedResult === 'draw'
                    ? 'border-yellow-500 bg-yellow-50'
                    : 'border-gray-200 hover:border-yellow-300 hover:bg-yellow-50'
                }`}
              >
                <div className="text-center">
                  <HandRaisedIcon className="w-12 h-12 mx-auto mb-3 text-yellow-600" />
                  <div className="font-semibold text-yellow-800 mb-1">平局</div>
                  <div className="text-sm text-gray-600">双方打平</div>
                </div>
              </button>

              {/* 我方失败 */}
              <button
                onClick={() => handleResultSelect('loss')}
                className={`p-6 rounded-lg border-2 transition-all ${
                  selectedResult === 'loss'
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
                }`}
              >
                <div className="text-center">
                  <XMarkIcon className="w-12 h-12 mx-auto mb-3 text-red-600" />
                  <div className="font-semibold text-red-800 mb-1">我方失败</div>
                  <div className="text-sm text-gray-600">团队失败</div>
                </div>
              </button>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex space-x-4">
            <button
              onClick={handleSubmit}
              disabled={!selectedResult || isLoading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <CheckIcon className="w-5 h-5" />
              <span>确认结果</span>
            </button>

            <button
              onClick={handleCancel}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <XMarkIcon className="w-5 h-5" />
              <span>取消</span>
            </button>
          </div>
        </>
      ) : (
        /* 确认对话框 */
        <div className="text-center">
          <div className="mb-6">
            {getResultIcon(selectedResult!)}
          </div>
          
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            确认比赛结果
          </h3>
          
          <p className="text-gray-600 mb-6">
            {getResultDescription(selectedResult!)}
          </p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="text-yellow-800 text-sm">
              <div className="font-medium mb-1">注意：</div>
              <div>比赛结果一旦保存将无法修改，请确认结果正确。</div>
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={handleConfirmSubmit}
              disabled={isSubmitting}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>保存中...</span>
                </>
              ) : (
                <>
                  <CheckIcon className="w-5 h-5" />
                  <span>确认保存</span>
                </>
              )}
            </button>

            <button
              onClick={handleCancel}
              disabled={isSubmitting}
              className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <XMarkIcon className="w-5 h-5" />
              <span>返回</span>
            </button>
          </div>
        </div>
      )}

      {/* 操作提示 */}
      {!showConfirmation && (
        <div className="text-xs text-gray-500 mt-6 pt-4 border-t">
          <div className="space-y-1">
            <div>• 请根据实际比赛结果选择获胜方或平局</div>
            <div>• 比赛结果将被永久保存用于统计分析</div>
            <div>• 保存后将自动进行队列轮换</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchResult;